import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
// 1. Import AsyncImage
import { AsyncImage } from "loadable-image";

const ENVIORNMENT = process.env.REACT_APP_ENV;
const BASE_URL = process.env.REACT_APP_BACKEND_BASE_URL;

const ImageWithFallback = ({
  src,
  alt,
  width,
  height,
  className,
  exercisePlan = false,
  anonymous = true,
}) => {
  // We will now use a separate state for handling the fallback image display
  const [useFallback, setUseFallback] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(src);

  // Reset state when src changes
  useEffect(() => {
    setUseFallback(false);
    setCurrentSrc(src);
  }, [src]);

  const handleImageClick = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = (e) => {
    // Only close if the click is on the backdrop, not the image content
    if (!e.target.closest(".modal-content")) {
      setIsModalOpen(false);
    }
  };

  // Determine the final URL the image should try to load
  const imageUrl =
    ENVIORNMENT === "LOCAL" ? `${BASE_URL}${currentSrc}` : currentSrc;

  // Determine the local fallback image path
  const fallbackSrc = exercisePlan ? "/exerciseDefault.png" : "/MealOne.png";

  // Determine the source used for the final render (either the original URL or the fallback)
  const imageSrc = useFallback ? fallbackSrc : imageUrl;

  // 2. Define the base props for the AsyncImage/img element
  const baseImageProps = {
    // We use imageSrc here to display the fallback if an error occurred in AsyncImage
    src: imageSrc, 
    alt: alt,
    onClick: handleImageClick,
    width: width,
    height: height,
    // Keep all your original styling classes
    className: `${className} object-cover transition duration-300 cursor-pointer !relative`,
    loading: "lazy",
    key: src,
  };

  const ImageContainer = ({ children, loaded, error }) => {
  if (loaded) return children;
  
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <img 
        {...baseImageProps}
        src={error ? fallbackSrc : imageUrl}
      />
    </div>
  );
};

  return (
    <>
      <AsyncImage
        {...baseImageProps}
        src={imageUrl} 
        onLoadError={() => setUseFallback(true)} 
        // {...(ENVIORNMENT === "PRODUCTION" && anonymous ? { crossOrigin: "anonymous" } : {})}
        container={({ image, loaded, error }) => {
          if (loaded) return image;
          
          return (
            <img 
              {...baseImageProps}
              src={error ? fallbackSrc : imageUrl} 
              crossOrigin="anonymous"
            />
          );
        }}
      />

      {/* --- Modal Portal (No change needed here) --- */}
      {isModalOpen &&
        createPortal(
          <div
            className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 cursor-pointer"
            onClick={handleCloseModal}
          >
            <div className="relative max-w-[90%] md:max-w-[60vw] modal-content">
              <img
                src={imageSrc} 
                alt={alt}
                className="max-w-full max-h-[80vh] object-contain rounded"
                onClick={(e) => e.stopPropagation()}
                key={src}
                loading="lazy"
              />
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute top-2 right-2 bg-white text-black rounded-full w-8 h-8 flex items-center justify-center shadow-md hover:bg-gray-200 transition"
                aria-label="Close image"
              >
                ✕
              </button>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default ImageWithFallback;